import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataTable, IDataTableColumn, LoadingIndicator, useObservable } from '@lib/common';
import { CostCenterOption, getCostCenterOptionsQuery } from '@lib/state';
import { Typography, Button, Box, Grid, TextField, IconButton } from '@material-ui/core';
import React, { useCallback, useState } from 'react';

interface Props {
  costCenterKey: string;
  accountId?: string;
  costCenterOptions?: Array<CostCenterOption>;
  handleOptionChange: (optionsList: Array<CostCenterOption>, costCenterKey: string) => void;
}

interface CostCenterOptionsForm {
  costCenterOption: string;
}

const optionSort = (a: CostCenterOption, b: CostCenterOption) => (a.order ?? 0) - (b.order ?? 0);

export function CostCenterOptionsListForm({
  costCenterKey,
  accountId,
  costCenterOptions = [],
  handleOptionChange,
}: Props) {
  const isLoading = useObservable(getCostCenterOptionsQuery().isLoading);

  const handleChange = useCallback(
    (optionsList: Array<CostCenterOption>) => {
      //Prevent race condition where MUI table tries to select row after options have been updated
      //See: https://github.com/mui/mui-x/issues/2714#issuecomment-928223613
      setTimeout(() => {
        handleOptionChange(optionsList.sort(optionSort), costCenterKey);
      });
    },
    [handleOptionChange, costCenterKey]
  );

  const onAddOption = useCallback(
    ({ costCenterOption }: CostCenterOptionsForm) => {
      const newOption: CostCenterOption = {
        costCenterFieldKey: costCenterKey,
        corporateAccountId: accountId,
        label: costCenterOption,
        value: costCenterOption,
        order: costCenterOptions.length + 1,
      };

      handleChange([...costCenterOptions, newOption]);
    },
    [accountId, costCenterOptions, costCenterKey, handleChange]
  );

  const onOrderChange = useCallback(
    (oldOrder: number, newOrder: number) => {
      if (newOrder < 1 || newOrder > costCenterOptions.length) return; // Prevent moving to an invalid order

      let options = [...costCenterOptions];

      const direction = oldOrder < newOrder ? 1 : -1;
      const start = oldOrder < newOrder ? oldOrder - 1 : newOrder - 1;

      const oldOptions = options.splice(start, 2);

      const newOptions = oldOptions.map(option => {
        let order = option.order ?? 0;
        if (order === oldOrder) {
          order += direction;
        } else {
          order -= direction;
        }

        return { ...option, order };
      });

      handleChange(options.concat(newOptions));
    },
    [costCenterOptions, handleChange]
  );

  const onRemove = useCallback(
    (orderNumber: number) => {
      const optionIndex = costCenterOptions.findIndex(x => x.order === orderNumber);
      const firstHalfOptions = costCenterOptions.slice(0, optionIndex);
      const secondHalfOptions = costCenterOptions
        .slice(optionIndex + 1, costCenterOptions.length)
        .map(x => ({ ...x, order: (x.order || 2) - 1 }));

      handleChange(firstHalfOptions.concat(secondHalfOptions));
    },
    [costCenterOptions, handleChange]
  );

  const columns: Array<IDataTableColumn<CostCenterOption>> = [
    {
      title: 'Order',
      valueFactory: option => (
        <GetOptionOrderActions order={option.order ?? 0} onOrderChange={onOrderChange} />
      ),
      sortable: true,
      colSpan: 0.5,
    },
    {
      title: 'Value',
      valueFactory: option => <Typography>{option.label}</Typography>,
    },
    {
      title: 'Actions',
      valueFactory: option => (
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => onRemove(option.order ?? 0)}
          size="small"
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <CostCenterOptionForm onSubmit={onAddOption} />
          <Box sx={{ height: '20em', overflow: 'scroll' }}>
            <DataTable
              columns={columns}
              items={costCenterOptions}
              data-testid="costCenterOptionsTable"
              getItemKey={x => `${x.label?.replaceAll(' ', '')}${x.order || 0}`}
              isRowSelectable={() => false}
            />
          </Box>
        </>
      )}
    </>
  );
}

interface CostCenterOptionFormProps {
  onSubmit: (data: CostCenterOptionsForm) => void;
}

function CostCenterOptionForm({ onSubmit }: CostCenterOptionFormProps) {
  const [option, setOption] = useState<string>('');

  const handleSubmit = useCallback(() => {
    if (option !== '') {
      setOption('');
      onSubmit({ costCenterOption: option });
    }
  }, [onSubmit, option, setOption]);

  return (
    <Grid container spacing={2} style={{ marginBottom: '.5em' }}>
      <Grid item xs={8}>
        <TextField
          name="costCenterOption"
          label="Cost Center Option"
          fullWidth
          value={option}
          onChange={e => setOption(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          data-testid="costCenterOptionInput"
        />
      </Grid>
      <Grid item xs={4}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          style={{ marginTop: '.5em' }}
        >
          Add
        </Button>
      </Grid>
    </Grid>
  );
}

interface GetOptionOrderActionsProps {
  order: number;
  onOrderChange: (oldOrder: number, newOrder: number) => void;
}

function GetOptionOrderActions({ order, onOrderChange }: GetOptionOrderActionsProps) {
  return (
    <Grid container>
      <Grid item xs={12}>
        <IconButton
          color="primary"
          onClick={() => onOrderChange(order, order - 1)}
          size="small"
          disabled={order <= 1}
        >
          <FontAwesomeIcon icon="chevron-up" />
        </IconButton>
      </Grid>
      <Grid item xs={12}>
        <IconButton color="primary" onClick={() => onOrderChange(order, order + 1)} size="small">
          <FontAwesomeIcon icon="chevron-down" />
        </IconButton>
      </Grid>
    </Grid>
  );
}
